import { isEqual } from 'lodash';
import { connect } from 'react-redux';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { employmentHasAccessToHomePage } from '@frontend/services/api/accessCheckApiService';
import { FrontendSettingsContext } from '@frontend/utils/hooks/useFrontendSettings';
import type { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import type { IReduxState } from '../../../configureStore';
import type { ReduxStatus } from '../../../store/utils/reduxStatus';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import type { ServiceMapType } from './newProcess/api/newProcessApi';
import { AbortableFetch } from '../../../store/fetch';
import { serviceConsumersSelector } from '../../../store/serviceConsumers/serviceConsumerActions';
import { AdminHomepage } from '../../AdminHomepage';
import { Component } from '../../elements/Component';
import { TwoFactorAuthenticationAlert } from '../profile/TwoFactorAuthenticationAlert';
import { NewProcessApi } from './newProcess/api/newProcessApi';
import { NewProcessContainer } from './newProcess/NewProcessContainer';
import { HomeTaskList } from './taskList/HomeTaskList';

interface IHomeReduxProps {
    authentication: {
        employmentPractitionerRoles?: PractitionerRole[];
        hasEmploymentRights: boolean;
        id: string;
        isManager: boolean;
        organizationId: string;
        selectedEmploymentId: string;
    };
    serviceConsumers: IServiceConsumer[];
    serviceConsumersLoadStatus: ReduxStatus;
}

interface IHomeContainerState {
    isTwoFactorAuthenticationEnabled: boolean;
    serviceMap: ServiceMapType;
}

const mapStateToProps = (state: IReduxState): IHomeReduxProps => ({
    authentication: {
        employmentPractitionerRoles: state.authentication.employmentPractitionerRoles,
        hasEmploymentRights: state.authentication.hasEmploymentRights,
        id: state.authentication.id,
        isManager: state.authentication.isManager,
        organizationId: state.authentication.organizationId,
        selectedEmploymentId: state.authentication.selectedEmploymentId,
    },
    serviceConsumers: serviceConsumersSelector(state).serviceConsumers,
    serviceConsumersLoadStatus: state.serviceConsumers.status,
});

type IHomeProps = IWithRouterProps & IHomeReduxProps;

class InnerHomePage extends Component<IHomeProps, IHomeContainerState> {
    static contextType = FrontendSettingsContext;
    declare context: React.ContextType<typeof FrontendSettingsContext>;

    public state: IHomeContainerState = {
        isTwoFactorAuthenticationEnabled: true,
        serviceMap: {},
    };

    private abortableFetch = new AbortableFetch();
    private abortController = new AbortController();
    private readonly newProcessApi = new NewProcessApi(this.abortableFetch);
    private readonly isOnlyOrganizationPayoutManager =
        !this.props.authentication.hasEmploymentRights &&
        !!this.props.authentication.employmentPractitionerRoles?.find(
            (practitionerRole) => practitionerRole === PractitionerRole.organizationPayoutManager
        );

    public async componentDidMount() {
        await this.checkHomePageAccess(this.props);
        await this.loadTwoFactorAuthenticationSettings(this.props);
        await this.setState({ serviceMap: await this.newProcessApi.createServiceMap() });
    }

    public async componentDidUpdate(prevProps: Readonly<IHomeProps>) {
        if (
            !isEqual(prevProps.serviceConsumers, this.props.serviceConsumers) ||
            prevProps.serviceConsumersLoadStatus !== this.props.serviceConsumersLoadStatus
        ) {
            await this.checkHomePageAccess(this.props);
        }
    }

    public async componentWillUnmount() {
        this.abortableFetch.abort();
        this.abortController.abort();
    }

    public render() {
        const episodeCategory = this.props.router.searchParams.getOptional('episodeCategory') as EpisodeCategory;
        if (!this.props.authentication.isManager) {
            return (
                <PageTemplate>
                    {this.context?.frontendSettings.enable2FaAlert && !this.state.isTwoFactorAuthenticationEnabled && (
                        <TwoFactorAuthenticationAlert
                            hasIcon={true}
                            practitionerId={this.props.authentication.id}
                            style={{ marginBottom: -24 }}
                        />
                    )}

                    <NewProcessContainer defaultEpisodeCategory={episodeCategory} />
                    {Object.keys(this.state.serviceMap).length > 0 && <HomeTaskList />}
                </PageTemplate>
            );
        } else {
            return <AdminHomepage />;
        }
    }

    private async checkHomePageAccess(props: IHomeProps) {
        const hasAccess =
            props.authentication.isManager ||
            (await employmentHasAccessToHomePage(
                props.authentication.selectedEmploymentId,
                this.abortController.signal
            ));
        if (this.isOnlyOrganizationPayoutManager) {
            props.router.navigate(`/organizations/${props.authentication.organizationId}/view`);
        } else if (!hasAccess && !props.authentication.isManager) {
            props.router.navigate('/requests');
        }
    }

    private async loadTwoFactorAuthenticationSettings(props: IHomeProps) {
        const url = `/api/otpMethods/${props.authentication.id}/twoFactorAuthenticationSettings`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        const isTwoFactorAuthenticationEnabled = result.json.twoFactorAuthenticationType !== null;

        await this.setState({ isTwoFactorAuthenticationEnabled });
    }
}

export const HomePage = connect(mapStateToProps)(withRouter(InnerHomePage));
